import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { EmailTemplatesManager } from '../components/EmailTemplatesManager';
import { Building2, Users2, Plus, Pencil, ShieldAlert, ShieldCheck, X, Trash2, Globe2, AlertTriangle, Info, Settings } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    contacts: number;
    campaigns: number;
    whatsappSessions: number;
  };
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxConnections: number;
    maxCampaigns: number;
  };
}

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  criadoEm: string;
  tenant?: {
    id: string;
    name: string;
  };
}

interface TenantFormData {
  slug: string;
  name: string;
  domain: string;
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
  };
}

interface UserTenant {
  id: string;
  name: string;
  role: string;
}

export function SuperAdminPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Tabs
  const [activeTab, setActiveTab] = useState<'empresas' | 'tenants_list' | 'users' | 'email'>('empresas');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageTenantsModal, setShowManageTenantsModal] = useState(false);

  // Selection
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userTenants, setUserTenants] = useState<UserTenant[]>([]);

  // Dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void | Promise<void>;
    type: 'info' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void | Promise<void>, type: 'info' | 'danger' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        await onConfirm();
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      type
    });
  };

  // Forms
  const [formData, setFormData] = useState<TenantFormData>({
    slug: '',
    name: '',
    domain: '',
    adminUser: { nome: '', email: '', senha: '' },
    quotas: { maxUsers: 10, maxContacts: 1000, maxCampaigns: 50, maxConnections: 5 }
  });

  const [associationForm, setAssociationForm] = useState({
    tenantId: '',
    role: 'USER'
  });

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, loadUsers]);

  const buildAuthHeaders = useCallback((includeJson = false) => {
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }

    return headers;
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const response = await fetch('/api/tenants', {
        headers: buildAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeaders]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/users/global?pageSize=100', {
        headers: buildAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data.users);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar usuários');
    }
  }, [buildAuthHeaders]);

  const loadUserTenants = async (userId: string) => {
    try {
      const response = await fetch(`/api/user-tenants/users/${userId}/tenants`, {
        headers: buildAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUserTenants(data.tenants); // userTenants controller returns { tenants: [] }
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar tenants do usuário');
    }
  };

  const handleCreateTenant = async () => {
    try {
      const response = await fetch('/api/tenants', {
        method: 'POST',
        headers: buildAuthHeaders(true),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Tenant criado com sucesso!');
        setShowCreateModal(false);
        resetForm();
        loadTenants();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar tenant');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar tenant');
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;
    try {
      const response = await fetch(`/api/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: buildAuthHeaders(true),
        body: JSON.stringify({
          name: formData.name,
          domain: formData.domain || null,
          active: selectedTenant.active,
          quotas: formData.quotas
        })
      });

      if (response.ok) {
        toast.success('Tenant atualizado com sucesso!');
        setShowEditModal(false);
        setSelectedTenant(null);
        resetForm();
        loadTenants();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao atualizar tenant');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar tenant');
    }
  };

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: buildAuthHeaders(true),
        body: JSON.stringify({ ...tenant, active: !tenant.active })
      });

      if (response.ok) {
        toast.success(`Tenant ${!tenant.active ? 'ativado' : 'desativado'} com sucesso!`);
        loadTenants();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao alterar status');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao alterar status');
    }
  };

  const handleAssociateUser = async () => {
    if (!selectedUser || !associationForm.tenantId) {
      toast.error('Selecione um tenant');
      return;
    }

    try {
      const response = await fetch('/api/user-tenants/associations', {
        method: 'POST',
        headers: buildAuthHeaders(true),
        body: JSON.stringify({
          userId: selectedUser.id,
          tenantId: associationForm.tenantId,
          role: associationForm.role
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Usuário associado com sucesso!');
        loadUserTenants(selectedUser.id);
        setAssociationForm({ tenantId: '', role: 'USER' });
      } else {
        toast.error(data.message || 'Erro ao associar usuário');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro de conexão');
    }
  };

  const handleRemoveUserTenant = async (tenantId: string) => {
    if (!selectedUser) return;
    
    showConfirm(
      'Remover Acesso',
      'Tem certeza que deseja remover o acesso do usuário a este tenant?',
      async () => {
        try {
          const response = await fetch(`/api/user-tenants/associations/${selectedUser.id}/${tenantId}`, {
            method: 'DELETE',
            headers: buildAuthHeaders()
          });

          if (response.ok) {
            toast.success('Associação removida com sucesso!');
            loadUserTenants(selectedUser.id);
          } else {
            toast.error('Erro ao remover associação');
          }
        } catch (e) {
          toast.error('Erro de conexão');
        }
      }
    );
  };

  const openEditModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      slug: tenant.slug,
      name: tenant.name,
      domain: tenant.domain || '',
      adminUser: { nome: '', email: '', senha: '' },
      quotas: tenant.quota || { maxUsers: 10, maxContacts: 1000, maxCampaigns: 50, maxConnections: 5 }
    });
    setShowEditModal(true);
  };

  const openManageTenantsModal = (user: User) => {
    setSelectedUser(user);
    loadUserTenants(user.id);
    setShowManageTenantsModal(true);
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      name: '',
      domain: '',
      adminUser: { nome: '', email: '', senha: '' },
      quotas: { maxUsers: 10, maxContacts: 1000, maxCampaigns: 50, maxConnections: 5 }
    });
  };

  const getUsagePercentage = (current: number, max: number) => Math.round((current / max) * 100);
  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-400 bg-red-500/20';
    if (percentage >= 70) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-6 bg-white/5 p-12 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-blue-500/10 rounded-full animate-pulse"></div>
            </div>
          </div>
          <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando painel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-4 sm:p-8 font-oswald">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative z-10">
            <h1 className="text-4xl font-bold text-white tracking-tight uppercase">
              Super Admin
            </h1>
            <p className="text-gray-400 mt-2 font-medium tracking-wide uppercase text-xs opacity-80">
              Gerenciamento Global do Sistema e Tenants
            </p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap gap-4 bg-white/5 p-3 rounded-[2rem] border border-white/10 backdrop-blur-md w-fit shadow-2xl shadow-black/20">
          <button
            onClick={() => setActiveTab('empresas')}
            className={`px-6 py-2.5 rounded-xl font-bold font-oswald text-sm transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'empresas' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 scale-105' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            🏢 Empresas
          </button>
          <button
            onClick={() => setActiveTab('tenants_list')}
            className={`px-6 py-2.5 rounded-xl font-bold font-oswald text-sm transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'tenants_list' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 scale-105' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            📋 Tenants
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-2.5 rounded-xl font-bold font-oswald text-sm transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'users' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 scale-105' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            👥 Usuários
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`px-6 py-2.5 rounded-xl font-bold font-oswald text-sm transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'email' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 scale-105' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            📧 Templates
          </button>
        </div>

        {/* Tab Content: Empresas (Original Dashboard View) */}
        {activeTab === 'empresas' && (
          <div className="animate-fade-in space-y-10">
            <div className="flex justify-between items-center bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl">
              <div>
                <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                  <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                  Visão Geral
                </h2>
                <p className="text-gray-400 text-sm font-medium mt-1 uppercase tracking-wider opacity-70">Monitoramento de status e quotas do sistema</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-105 relative overflow-hidden group/btn border border-blue-400/50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                <Plus className="w-5 h-5" />
                Nova Empresa
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Building2 className="w-16 h-16 text-blue-400" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Total de Empresas</p>
                  <p className="text-4xl font-bold text-white font-oswald tracking-tight">{tenants.length}</p>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <ShieldCheck className="w-16 h-16 text-green-400" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Empresas Ativas</p>
                  <p className="text-4xl font-bold text-green-400 font-oswald tracking-tight">{tenants.filter(t => t.active).length}</p>
                </div>
              </div>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tenants.map((tenant) => (
                <div key={tenant.id} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8 hover:shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
                  <div className="flex justify-between items-start mb-8">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-600/10 rounded-2xl border border-blue-500/20 flex items-center justify-center text-blue-400">
                        <Building2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight group-hover:text-blue-400 transition-colors">{tenant.name}</h3>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] font-oswald mt-1">{tenant.slug}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm ${
                      tenant.active 
                        ? 'bg-green-500/10 text-green-400 border-green-500/20 shadow-green-900/20' 
                        : 'bg-red-500/10 text-red-400 border-red-500/20 shadow-red-900/20'
                    }`}>
                      {tenant.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>

                  {tenant._count && tenant.quota && (
                    <div className="space-y-6 mb-10">
                      <div className="space-y-2">
                        <div className="flex justify-between items-end">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                            <Users2 className="w-3 h-3" /> Usuários
                          </span>
                          <span className="text-xs font-bold text-white font-oswald">{tenant._count.users} / {tenant.quota.maxUsers}</span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2 border border-white/5 p-0.5">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${getUsageColor(getUsagePercentage(tenant._count.users, tenant.quota.maxUsers)).split(' ')[1].replace('bg-', 'bg-')}`} 
                            style={{ width: `${Math.min(getUsagePercentage(tenant._count.users, tenant.quota.maxUsers), 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-end">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                            <Globe2 className="w-3 h-3" /> Contatos
                          </span>
                          <span className="text-xs font-bold text-white font-oswald">{tenant._count.contacts} / {tenant.quota.maxContacts}</span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2 border border-white/5 p-0.5">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${getUsageColor(getUsagePercentage(tenant._count.contacts, tenant.quota.maxContacts)).split(' ')[1].replace('bg-', 'bg-')}`} 
                            style={{ width: `${Math.min(getUsagePercentage(tenant._count.contacts, tenant.quota.maxContacts), 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 justify-end pt-6 border-t border-white/5">
                    <button 
                      onClick={() => openEditModal(tenant)} 
                      className="px-6 py-2 bg-white/5 text-blue-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-blue-600 hover:text-white border border-white/10 transition-all font-oswald flex items-center gap-2"
                    >
                      <Pencil className="w-3 h-3" />
                      Editar
                    </button>
                    <button 
                      onClick={() => handleToggleTenantStatus(tenant)} 
                      className={`px-6 py-2 bg-white/5 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl border border-white/10 transition-all font-oswald flex items-center gap-2 ${
                        tenant.active 
                          ? 'text-red-400 hover:bg-red-600 hover:text-white' 
                          : 'text-green-400 hover:bg-green-600 hover:text-white'
                      }`}
                    >
                      {tenant.active ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                      {tenant.active ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab Content: Tenants List */}
        {activeTab === 'tenants_list' && (
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl animate-fade-in">
            <div className="px-10 py-8 border-b border-white/10 flex justify-between items-center bg-white/5 backdrop-blur-md">
              <div>
                <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                  <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                  Lista de Tenants
                </h2>
                <p className="text-gray-400 text-xs font-medium mt-1 uppercase tracking-wider opacity-70">Gerenciamento detalhado de instâncias</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-[10px] uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-105 relative overflow-hidden group/btn border border-blue-400/50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                + Novo Tenant
              </button>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5 sticky top-0 z-10 backdrop-blur-md">
                  <tr>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Nome</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Domain</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Status</th>
                    <th className="px-10 py-6 text-right text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {tenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-10 py-6 whitespace-nowrap">
                        <div className="text-sm font-bold text-white uppercase tracking-wide font-oswald">{tenant.name}</div>
                        <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider font-oswald mt-1">{tenant.slug}</div>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <span className="text-sm text-gray-400 font-medium">{tenant.domain || 'N/A'}</span>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm ${
                          tenant.active 
                            ? 'bg-green-500/10 text-green-400 border-green-500/20 shadow-green-900/20' 
                            : 'bg-red-500/10 text-red-400 border-red-500/20 shadow-red-900/20'
                        }`}>
                          {tenant.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => openEditModal(tenant)}
                            className="p-2 bg-white/5 text-blue-400 hover:bg-blue-600 hover:text-white rounded-xl border border-white/10 transition-all"
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleToggleTenantStatus(tenant)}
                            className={`p-2 bg-white/5 rounded-xl border border-white/10 transition-all ${
                              tenant.active 
                                ? 'text-red-400 hover:bg-red-600 hover:text-white' 
                                : 'text-green-400 hover:bg-green-600 hover:text-white'
                            }`}
                            title={tenant.active ? 'Desativar' : 'Ativar'}
                          >
                            {tenant.active ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content: Users List */}
        {activeTab === 'users' && (
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl animate-fade-in">
            <div className="px-10 py-8 border-b border-white/10 bg-white/5 backdrop-blur-md">
              <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                Todos os Usuários
              </h2>
              <p className="text-gray-400 text-xs font-medium mt-1 uppercase tracking-wider opacity-70">Listagem global de todos os usuários do sistema</p>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5 sticky top-0 z-10 backdrop-blur-md">
                  <tr>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Usuário</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Role</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Status</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Criado em</th>
                    <th className="px-10 py-6 text-right text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-10 py-6 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600/20 to-blue-900/20 border border-blue-500/20 flex items-center justify-center text-blue-400 font-bold font-oswald text-lg">
                            {user.nome.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-white uppercase tracking-wide font-oswald">{user.nome}</div>
                            <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider font-oswald mt-1">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-sm shadow-blue-900/20">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm ${
                          user.ativo 
                            ? 'bg-green-500/10 text-green-400 border-green-500/20 shadow-green-900/20' 
                            : 'bg-red-500/10 text-red-400 border-red-500/20 shadow-red-900/20'
                        }`}>
                          {user.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap text-sm text-gray-400 font-medium">
                        {new Date(user.criadoEm).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => openManageTenantsModal(user)}
                          className="px-6 py-2.5 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-900/40 transition-all hover:scale-105 font-oswald border border-blue-400/50 relative overflow-hidden group/btn flex items-center gap-2"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                          <Settings className="w-4 h-4" />
                          Gerenciar Tenants
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content: Email Templates */}
        {activeTab === 'email' && (
          <div className="animate-fade-in space-y-10">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] shadow-2xl p-10">
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                  <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                  Templates de Email
                </h2>
                <p className="text-gray-400 text-sm font-medium mt-1 uppercase tracking-wider opacity-70">Gerenciamento global de templates para o sistema</p>
              </div>
              <EmailTemplatesManager isGlobal={true} />
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal (Reused) */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-xl relative flex flex-col custom-scrollbar">
            <div className="p-10 border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-10">
              <h2 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
                <span className="p-3 bg-blue-600/10 rounded-[1.25rem] border border-blue-500/20 text-2xl shadow-[0_0_20px_rgba(37,99,235,0.2)]">🏢</span>
                {showCreateModal ? 'Novo Tenant' : 'Editar Tenant'}
              </h2>
            </div>

            <div className="p-10 space-y-10">
              <div className="space-y-8">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome da Empresa</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner" placeholder="Ex: Minha Empresa" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Identificador (Slug)</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all disabled:opacity-50 shadow-inner" placeholder="Ex: minha-empresa" value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value })} disabled={showEditModal} />
                  </div>
                  <div className="md:col-span-2 space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Domínio Customizado (Opcional)</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner" placeholder="Ex: app.minhaempresa.com" value={formData.domain} onChange={e => setFormData({ ...formData, domain: e.target.value })} />
                  </div>
                </div>
              </div>

              {showCreateModal && (
                <div className="space-y-8 pt-10 border-t border-white/5">
                  <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                    <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                    Administrador do Sistema
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome Completo</label>
                      <input type="text" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner" value={formData.adminUser.nome} onChange={e => setFormData({ ...formData, adminUser: { ...formData.adminUser, nome: e.target.value } })} />
                    </div>
                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Email Principal</label>
                      <input type="email" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner" value={formData.adminUser.email} onChange={e => setFormData({ ...formData, adminUser: { ...formData.adminUser, email: e.target.value } })} />
                    </div>
                    <div className="md:col-span-2 space-y-3">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Senha de Acesso</label>
                      <input type="password" placeholder="••••••••" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner" value={formData.adminUser.senha} onChange={e => setFormData({ ...formData, adminUser: { ...formData.adminUser, senha: e.target.value } })} />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-8 pt-10 border-t border-white/5">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Limites e Quotas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Usuários</label>
                    <input type="number" className="w-full bg-white/5 border border-white/10 p-4 text-white rounded-2xl focus:border-blue-500 outline-none transition-all shadow-inner" value={formData.quotas.maxUsers} onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxUsers: parseInt(e.target.value) } })} />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Contatos</label>
                    <input type="number" className="w-full bg-white/5 border border-white/10 p-4 text-white rounded-2xl focus:border-blue-500 outline-none transition-all shadow-inner" value={formData.quotas.maxContacts} onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxContacts: parseInt(e.target.value) } })} />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Campanhas</label>
                    <input type="number" className="w-full bg-white/5 border border-white/10 p-4 text-white rounded-2xl focus:border-blue-500 outline-none transition-all shadow-inner" value={formData.quotas.maxCampaigns} onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxCampaigns: parseInt(e.target.value) } })} />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Conexões</label>
                    <input type="number" className="w-full bg-white/5 border border-white/10 p-4 text-white rounded-2xl focus:border-blue-500 outline-none transition-all shadow-inner" value={formData.quotas.maxConnections} onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxConnections: parseInt(e.target.value) } })} />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-10 border-t border-white/10 bg-white/5 backdrop-blur-md flex justify-end gap-4 sticky bottom-0 z-10">
              <button 
                onClick={() => { setShowCreateModal(false); setShowEditModal(false); }} 
                className="px-8 py-4 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10 font-oswald"
              >
                Cancelar
              </button>
              <button 
                onClick={showCreateModal ? handleCreateTenant : handleUpdateTenant} 
                className="px-10 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                <span>Salvar Configurações</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Tenants Modal */}
      {showManageTenantsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-xl relative flex flex-col custom-scrollbar">
            <div className="p-10 border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-10 flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
                  <span className="p-3 bg-blue-600/10 rounded-[1.25rem] border border-blue-500/20 text-2xl shadow-[0_0_20px_rgba(37,99,235,0.2)]">👥</span>
                  Gerenciar Tenants
                </h2>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Usuário:</span>
                  <span className="text-sm font-bold text-blue-400 font-oswald uppercase tracking-wide">{selectedUser.nome}</span>
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest ml-2">({selectedUser.email})</span>
                </div>
              </div>
              <button 
                onClick={() => setShowManageTenantsModal(false)} 
                className="p-3 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-10 space-y-10">
              {/* Current Associations */}
              <div className="space-y-6">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Acessos Atuais
                </h3>
                <div className="space-y-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                  {userTenants.length === 0 ? (
                    <div className="text-center py-12 bg-white/5 border border-dashed border-white/10 rounded-[2rem]">
                      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/5">
                        <Building2 className="w-8 h-8 text-gray-600" />
                      </div>
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] font-oswald">Nenhum tenant associado a este usuário</p>
                    </div>
                  ) : (
                    userTenants.map((item: UserTenant) => (
                      <div key={item.id} className="flex justify-between items-center p-6 bg-white/5 border border-white/10 rounded-[1.5rem] hover:bg-white/10 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-600/20 to-blue-900/20 border border-blue-500/20 rounded-xl flex items-center justify-center text-blue-400 font-bold font-oswald text-xl">
                            {item.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-white font-oswald uppercase tracking-wide">{item.name}</p>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.15em] bg-blue-500/10 text-blue-400 border border-blue-500/20 mt-1">
                              {item.role || 'USER'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveUserTenant(item.id)}
                          className="px-6 py-2.5 text-red-400 hover:text-white hover:bg-red-600 text-[10px] font-bold uppercase tracking-[0.2em] font-oswald rounded-xl transition-all border border-red-500/20 opacity-0 group-hover:opacity-100 flex items-center gap-2"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remover
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add New Association */}
              <div className="pt-10 border-t border-white/5 space-y-6">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-green-500 rounded-full shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
                  Adicionar Novo Acesso
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-white/5 p-6 rounded-[2rem] border border-white/10">
                  <div className="md:col-span-6">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1 mb-2">Empresa</label>
                    <select
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white focus:border-blue-500 outline-none transition-all appearance-none"
                      value={associationForm.tenantId}
                      onChange={e => setAssociationForm({ ...associationForm, tenantId: e.target.value })}
                    >
                      <option value="" className="bg-[#1e293b]">Selecione um Tenant...</option>
                      {tenants
                        .filter(t => !userTenants.some((ut) => ut.id === t.id))
                        .map(t => (
                          <option key={t.id} value={t.id} className="bg-[#1e293b]">{t.name} ({t.slug})</option>
                        ))}
                    </select>
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1 mb-2">Nível de Acesso</label>
                    <select
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white focus:border-blue-500 outline-none transition-all appearance-none"
                      value={associationForm.role}
                      onChange={e => setAssociationForm({ ...associationForm, role: e.target.value })}
                    >
                      <option value="USER" className="bg-[#1e293b]">Usuário Padrão</option>
                      <option value="ADMIN" className="bg-[#1e293b]">Administrador</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <button
                      onClick={handleAssociateUser}
                      className="w-full h-[46px] bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center justify-center border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50 disabled:hover:scale-100"
                      disabled={!associationForm.tenantId}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-10 border-t border-white/10 bg-white/5 backdrop-blur-md flex justify-end sticky bottom-0 z-10">
              <button 
                onClick={() => setShowManageTenantsModal(false)} 
                className="px-10 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald"
              >
                Fechar Painel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog 
        open={confirmDialog.isOpen} 
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}
      >
        <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border-white/10 rounded-[2.5rem] p-10 backdrop-blur-3xl shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
              <div className={`p-3 rounded-[1.25rem] border ${
                confirmDialog.type === 'danger' 
                  ? 'bg-red-500/10 border-red-500/20 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.2)]' 
                  : 'bg-blue-500/10 border-blue-500/20 text-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.2)]'
              }`}>
                {confirmDialog.type === 'danger' ? (
                  <AlertTriangle className="w-8 h-8" />
                ) : (
                  <Info className="w-8 h-8" />
                )}
              </div>
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 text-base font-medium leading-relaxed pt-4 pl-1">
              {confirmDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border-white/5 hover:bg-white/10 hover:border-white/10 transition-all font-oswald border">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={`px-10 py-4 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all duration-300 flex items-center gap-3 border relative overflow-hidden group/btn font-oswald ${
                confirmDialog.type === 'danger'
                  ? 'bg-red-600 border-red-500 shadow-lg shadow-red-900/40 hover:bg-red-700'
                  : 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/40 hover:bg-blue-700'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
